import { useEffect, useState } from "react";
import { Repeat, ArrowRight, ChevronLeft, CheckCircle, XCircle, Info } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  DRAFT: "Draft", PROPOSED: "Proposed", PARTIALLY_ACCEPTED: "Partially accepted",
  ALL_ACCEPTED: "All accepted", REJECTED: "Rejected", EXPIRED: "Expired",
  ADMIN_APPROVED: "Admin approved", ADMIN_REJECTED: "Admin rejected",
  PENDING_FULFILMENT: "Pending fulfilment", FULFILMENT_CONFIRMED: "Fulfilment confirmed",
  PENDING_SETTLEMENT: "Pending settlement", COMPLETED: "Completed",
  CANCELLED: "Cancelled", DISPUTED: "Disputed", FAILED: "Failed",
};

const TABS = [
  { value: "", label: "All" },
  { value: "PROPOSED", label: "Awaiting response" },
  { value: "ALL_ACCEPTED", label: "Pending admin" },
  { value: "PENDING_FULFILMENT", label: "In fulfilment" },
  { value: "PENDING_SETTLEMENT", label: "Pending settlement" },
  { value: "COMPLETED", label: "Completed" },
];

function CycleGraph({ legs, participants }) {
  const labelOf = (id) => {
    const p = participants.find((x) => x.company_id === id);
    return p?.company_name || id.slice(0, 8);
  };
  return (
    <div className="cycle-graph" data-testid="cycle-graph">
      {legs.map((l, i) => (
        <div key={l.id} className="leg-row">
          <span className="node-pill">{labelOf(l.seller_company_id)}</span>
          <ArrowRight size={16} />
          <span className="leg-value">₹{l.trade_value.toLocaleString("en-IN")}</span>
          <ArrowRight size={16} />
          <span className="node-pill">{labelOf(l.buyer_company_id)}</span>
          <span className="muted small" style={{ marginLeft: 8 }}>{l.listing_title}</span>
        </div>
      ))}
    </div>
  );
}

function CycleDetail({ id, onBack }) {
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    try { const r = await client.get(`/circular-trades/${id}`); setC(r.data); }
    catch (err) { setError(extractError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!c) return <div className="loading-block">Loading…</div>;

  const doAction = async (action, body) => {
    setBusy(true); setError("");
    try {
      const r = await client.post(`/circular-trades/${id}/${action}`, body || {});
      setC(r.data);
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  const me = c.participants.find((p) => p.is_viewer);
  const canAccept = me && me.acceptance_status === "PENDING";
  const canReject = me && me.acceptance_status === "PENDING";

  return (
    <div className="cycle-detail" data-testid="cycle-detail">
      <button className="link-button" onClick={onBack}><ChevronLeft size={14} /> Back</button>
      <div className="detail-head">
        <h2>{c.reference_number}</h2>
        <span className={`status-pill status-${c.status.toLowerCase()}`} data-testid="cycle-status">
          {STATUS_LABEL[c.status] || c.status}
        </span>
      </div>
      {error && <div className="form-error">{error}</div>}
      <section className="cycle-stats">
        <div><p className="eyebrow">TOTAL VALUE</p><strong>₹{c.total_trade_value.toLocaleString("en-IN")}</strong></div>
        <div><p className="eyebrow">PARTICIPANTS</p><strong>{c.participant_count}</strong></div>
        <div><p className="eyebrow">FACILITATOR FEE (10%)</p><strong>₹{c.total_facilitator_fee.toLocaleString("en-IN")}</strong></div>
        <div><p className="eyebrow">TYPE</p><strong>{c.cycle_type}</strong></div>
      </section>
      <section className="cycle-section">
        <h3>Cycle graph</h3>
        <CycleGraph legs={c.legs} participants={c.participants} />
      </section>
      <section className="cycle-section">
        <h3>Participants</h3>
        <table className="admin-table">
          <thead><tr><th>#</th><th>Company</th><th>Value</th><th>Response</th></tr></thead>
          <tbody>
            {c.participants.map((p) => (
              <tr key={p.id} data-testid={`participant-row-${p.position}`}>
                <td>{p.position + 1}</td>
                <td>{p.company_name}{p.is_viewer ? " (you)" : ""}</td>
                <td>₹{p.agreed_credit_value.toLocaleString("en-IN")}</td>
                <td>{p.acceptance_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {c.legs.some((l) => l.transaction) && (
        <section className="cycle-section">
          <h3>Fulfilment</h3>
          <table className="admin-table">
            <thead><tr><th>Leg</th><th>Seller</th><th>Buyer</th><th>Transaction</th><th>Fulfilment</th><th>Settlement</th></tr></thead>
            <tbody>
              {c.legs.map((l) => (
                <tr key={l.id}>
                  <td>{l.sequence + 1}</td>
                  <td>{l.seller_name}</td>
                  <td>{l.buyer_name}</td>
                  <td>{l.transaction?.status || "—"}</td>
                  <td>{l.transaction?.fulfilment_status || "—"}</td>
                  <td>{l.transaction?.settlement_status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {canAccept && (
        <section className="cycle-actions">
          <button className="primary-button" data-testid="accept-cycle-btn"
                    disabled={busy} onClick={() => doAction("accept", {})}>
            <CheckCircle size={14} /> Accept participation
          </button>
          <div className="reject-inline">
            <input placeholder="Rejection reason (min 5 chars)" value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    data-testid="reject-reason-input" />
            <button className="danger-button" data-testid="reject-cycle-btn"
                      disabled={busy || rejectReason.length < 5}
                      onClick={() => doAction("reject", { reason: rejectReason })}>
              <XCircle size={14} /> Reject
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default function CircularTradesPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await client.get("/circular-trades", { params: tab ? { status: tab } : {} });
      setItems(r.data || []);
    } catch (err) { setError(extractError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  if (detailId) return <CycleDetail id={detailId} onBack={() => { setDetailId(null); load(); }} />;

  return (
    <div className="circular-trades-page" data-testid="circular-trades-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="cycle-filter-tabs">
          {TABS.map((t) => (
            <button key={t.value || "all"} type="button"
                      className={tab === t.value ? "active" : ""}
                      data-testid={`filter-${t.value || "all"}`}
                      onClick={() => setTab(t.value)}>{t.label}</button>
          ))}
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        items.length === 0 ? (
          <div className="empty-state">
            <p><Info size={16} /> No circular trades yet.</p>
            <p className="muted small">Circular opportunities appear here as they are proposed to your company.</p>
          </div>
        ) : (
          <table className="admin-table" data-testid="circular-trades-table">
            <thead><tr><th>Ref</th><th>Status</th><th>Value</th><th>Fee</th><th>Your response</th><th></th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`cycle-row-${c.reference_number}`}>
                  <td>{c.reference_number}</td>
                  <td><span className={`status-pill status-${c.status.toLowerCase()}`}>{STATUS_LABEL[c.status] || c.status}</span></td>
                  <td>₹{c.total_trade_value.toLocaleString("en-IN")}</td>
                  <td>₹{c.total_facilitator_fee.toLocaleString("en-IN")}</td>
                  <td>{c.my_acceptance_status || "—"}</td>
                  <td><button className="link-button" data-testid={`open-cycle-${c.reference_number}`}
                              onClick={() => setDetailId(c.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
